import {
  BlockhashWithExpiryBlockHeight,
  Connection,
  PublicKey,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js';
import { COMMON_TX_ERRORS, TransactionError, handleSendTransaction } from '@mercurial-finance/optimist';
import { Owner } from './Owner';
import { ExecuteParams, OnTransaction, SwapResult } from '../types';
import { getSignature, getTokenBalanceChangesFromTransactionResponse, signTransaction } from './transactionHelpers';
import { JUPITER_COMMON_ERRORS, JUPITER_PROGRAM_V6_ID } from '../constants';
import { IDL_V6 } from '../idl/jupiter6';

function transformIntoJsTransaction(swapTransaction: Transaction | VersionedTransaction) {
  if ('version' in swapTransaction) {
    return swapTransaction;
  } else {
    // We rebuild a Transaction so what is passed to the wallet adapter will likely match what happens at the other side
    // This is to reduce the probability of serialized message mismatch for incorrect wallet adapter implementations
    return new Transaction({
      feePayer: swapTransaction.feePayer,
      recentBlockhash: swapTransaction.recentBlockhash,
    }).add(...swapTransaction.instructions);
  }
}

/** sign, send and await confirmation for an exchange */
export const executeTransaction = async ({
  connection,
  wallet,
  onTransaction,
  inputMint,
  outputMint,
  sourceAddress,
  destinationAddress,
  swapTransaction,
  blockhashWithExpiryBlockHeight,
  owner,
  wrapUnwrapSOL,
  minContextSlot,
}: {
  connection: Connection;
  wallet: ExecuteParams['wallet'];
  onTransaction?: OnTransaction;
  inputMint: PublicKey;
  outputMint: PublicKey;
  sourceAddress: PublicKey;
  destinationAddress: PublicKey;
  swapTransaction: Transaction | VersionedTransaction;
  blockhashWithExpiryBlockHeight: BlockhashWithExpiryBlockHeight;
  owner: Owner;
  wrapUnwrapSOL?: boolean;
  minContextSlot?: number;
}): Promise<SwapResult> => {
  let swapError: TransactionError | undefined = undefined;
  let swapResult: SwapResult | undefined = undefined;

  try {
    if (owner.signer) {
      signTransaction(swapTransaction, [owner.signer]);
    } else {
      if (!wallet) throw new Error('Signer wallet not found');
      swapTransaction = transformIntoJsTransaction(swapTransaction);
      swapTransaction = await wallet.signTransaction(swapTransaction);
    }

    const swapTxid = getSignature(swapTransaction);
    const skipPreflight = true;
    try {
      const swapTransactionSender = async () => {
        try {
          const transactionResponse = await handleSendTransaction({
            ...blockhashWithExpiryBlockHeight,
            connection,
            signedTransaction: swapTransaction,
            idl: IDL_V6,
            idlProgramId: JUPITER_PROGRAM_V6_ID,
            skipPreflight,
            minContextSlot,
          });

          if ('error' in transactionResponse) {
            // Map error to a local Jupiter error so ui can parse it.
            return mapCommonErrorToJupiterError(transactionResponse.error);
          }

          return transactionResponse.transactionResponse;
        } catch (e) {
          // catch for network error etc...
          return e as TransactionError;
        }
      };
      const swapPromise = swapTransactionSender();
      onTransaction?.(swapTxid, swapPromise);
      const transactionResponse = await swapPromise;

      if (transactionResponse instanceof Error) {
        throw transactionResponse;
      }

      const [sourceTokenBalanceChange, destinationTokenBalanceChange] = getTokenBalanceChangesFromTransactionResponse({
        txid: swapTxid,
        inputMint,
        outputMint,
        user: owner.publicKey,
        transactionResponse,
        hasWrappedSOL: !wrapUnwrapSOL,
      });

      swapResult = {
        txid: swapTxid,
        inputAddress: sourceAddress,
        outputAddress: destinationAddress,
        inputAmount: sourceTokenBalanceChange,
        outputAmount: destinationTokenBalanceChange,
      };
    } catch (e: any) {
      console.error(e);
      swapError = e;
    }

    if (swapError || !swapResult) {
      throw swapError || new Error('Swap failed');
    }

    return swapResult;
  } catch (error: any) {
    return { error: error as TransactionError };
  }
};

export const mapCommonErrorToJupiterError = (error: TransactionError) => {
  switch (error.message) {
    case COMMON_TX_ERRORS.TRANSACTION_NOT_CONFIRMED: {
      const errorDetails = JUPITER_COMMON_ERRORS['TransactionNotConfirmed'];
      return new TransactionError(errorDetails.msg, undefined, errorDetails.code, [JUPITER_PROGRAM_V6_ID.toBase58()]);
    }
    case COMMON_TX_ERRORS.TOO_MANY_ACCOUNT_LOCKS: {
      const errorDetails = JUPITER_COMMON_ERRORS['TooManyAccountLocked'];
      return new TransactionError(errorDetails.msg, undefined, errorDetails.code, [JUPITER_PROGRAM_V6_ID.toBase58()]);
    }
    case COMMON_TX_ERRORS.FROZEN_ACCOUNT: {
      const errorDetails = JUPITER_COMMON_ERRORS['FrozenAccount'];
      return new TransactionError(errorDetails.msg, undefined, errorDetails.code, error.programIds);
    }
    default: {
      return error;
    }
  }
};
